import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShieldCheck, Lock, Key, CheckCircle, Eye, EyeSlash, ArrowLeft, Copy, Warning, ChatCircleDots, User } from "@phosphor-icons/react";
import { LATEST_RELEASE_LABEL } from "@/version";

const PASSWORD_RULES = [
  { test: (p) => p.length >= 10, label: "At least 10 characters" },
  { test: (p) => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: (p) => /[a-z]/.test(p), label: "One lowercase letter" },
  { test: (p) => /[0-9]/.test(p), label: "One number" },
  { test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p), label: "One special character" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle } = useAuth();
  const [dbStatus, setDbStatus] = useState({ status: "checking", db_info: "" });

  // Session 69 — deep-link safe post-login destination. When a client
  // clicks "Open Ticket SUP-0044" in an email but isn't signed in, we
  // store their original target in `?next=/support?open=SUP-0044`, and
  // after successful auth we land them THERE instead of the generic
  // dashboard. Same machinery powers password-reset → return-to-page,
  // SSO → return-to-page, etc.
  const getPostLoginDest = () => {
    try {
      const params = new URLSearchParams(location.search);
      const next = params.get("next");
      // Only honour same-origin paths — never an external URL or a
      // protocol-relative `//attacker.com` open redirect.
      if (next && next.startsWith("/") && !next.startsWith("//")) {
        return next;
      }
    } catch { /* fall through */ }
    return "/dashboard";
  };

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await api.get("/health");
        setDbStatus(res.data);
      } catch { setDbStatus({ status: "error", database: "disconnected", db_info: "Unable to reach server" }); }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 15000);
    return () => clearInterval(interval);
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Multi-step
  const [authStep, setAuthStep] = useState("login");
  // Bootstrap JWT now lives in the eyvo_temp_auth HttpOnly cookie set
  // by the backend during the multi-step auth chain — the SPA never
  // sees or stores it. Removed `tempToken` state in Session 57.

  // Deep-link to signup screen via /signup URL or ?signup=1 query param
  useEffect(() => {
    const isSignupPath = location.pathname === "/signup";
    const wantsSignup = new URLSearchParams(location.search).get("signup") === "1";
    if (isSignupPath || wantsSignup) setAuthStep("client_signup");
  }, [location.pathname, location.search]);
  // Password change
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  // TOTP
  const [qrCode, setQrCode] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [totpCode, setTotpCode] = useState("");
  const [showRecoveryInput, setShowRecoveryInput] = useState(false);
  const [recoveryInput, setRecoveryInput] = useState("");
  // Domain check
  const [domainStatus, setDomainStatus] = useState(null);
  // Registration fields
  const [fullName, setFullName] = useState("");
  // SMS consent
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  // Signup
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirm, setSignupConfirm] = useState("");
  const [signupSmsConsent, setSignupSmsConsent] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [signupDomainStatus, setSignupDomainStatus] = useState(null);
  // Forgot password
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const checkDomain = async (emailValue) => {
    if (!emailValue || !emailValue.includes("@")) { setDomainStatus(null); return; }
    try {
      setDomainStatus("checking");
      const res = await api.post("/auth/check-domain", { email: emailValue });
      setDomainStatus(res.data);
    } catch { setDomainStatus(null); }
  };

  const checkSignupDomain = async (emailValue) => {
    if (!emailValue || !emailValue.includes("@")) { setSignupDomainStatus(null); return; }
    try {
      setSignupDomainStatus("checking");
      const res = await api.post("/auth/check-domain", { email: emailValue });
      setSignupDomainStatus(res.data);
    } catch { setSignupDomainStatus(null); }
  };

  const handleClientSignup = async (e) => {
    e.preventDefault();
    if (!signupName.trim()) return toast.error("Please enter your full name");
    if (!signupEmail.trim()) return toast.error("Please enter your email");
    if (!signupPassword) return toast.error("Please enter a password");
    if (signupPassword !== signupConfirm) return toast.error("Passwords do not match");
    const failing = PASSWORD_RULES.find(r => !r.test(signupPassword));
    if (failing) return toast.error(failing.label);
    try {
      setLoading(true);
      const res = await api.post("/auth/register-client", {
        name: signupName.trim(),
        email: signupEmail.trim(),
        phone: signupPhone.trim(),
        password: signupPassword,
        sms_consent: signupSmsConsent && signupPhone.trim().length > 0,
      });
      if (res.data.status === "totp_setup_required") {
        setEmail(signupEmail.trim());
        await loadTotpSetup();
        toast.success(`Account created for ${res.data.tenant_name || "your organization"}! Now set up 2FA.`);
      } else {
        // Backend accepted the registration but did not return the
        // expected status — surface it so the user isn't stuck on a
        // mystery "success" screen with no QR code.
        toast.error("Account created but 2FA setup could not be started. Please sign in to continue.");
      }
    } catch (err) {
      // Show the REAL error instead of a fake success. The previous
      // blanket "Account created successfully!" masked things like
      // "email already exists", weak password validation, or rate
      // limits, so the user would click Create Account, see a green
      // toast, and never get a QR code.
      const detail = err.response?.data?.detail || err.message || "Registration failed. Please try again.";
      toast.error(detail);
      console.warn("Registration error:", detail);
    } finally { setLoading(false); }
  };

  const handleGoogleSuccess = async (response) => {
    try {
      setLoading(true);
      await loginWithGoogle(response.credential);
      toast.success("Signed in successfully");
      navigate(getPostLoginDest());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google sign-in failed");
    } finally { setLoading(false); }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Please fill in all fields");
    try {
      setLoading(true);
      const res = await api.post("/auth/login", { email, password });
      // Session 57: temp_token now travels in an HttpOnly cookie set by
      // the backend. The frontend just routes on `status` and never
      // touches the bootstrap JWT directly.
      const { status } = res.data;
      if (status === "must_change_password") {
        setAuthStep("change_password");
        toast.info("Please set a new secure password");
      } else if (status === "totp_setup_required") {
        await loadTotpSetup();
      } else if (status === "totp_required") {
        setAuthStep("totp_verify");
      } else if (status === "authenticated") {
        // Cookie was set by the backend — no localStorage write needed.
        window.location.href = getPostLoginDest();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const loadTotpSetup = async () => {
    try {
      // Bootstrap JWT travels via the eyvo_temp_auth HttpOnly cookie set
      // earlier by /auth/login or /auth/register-client — no body token
      // required.
      const res = await api.post("/auth/setup-totp", {});
      setQrCode(res.data.qr_code);
      setSecretKey(res.data.secret_key);
      setRecoveryCodes(res.data.recovery_codes || []);
      setAuthStep("totp_setup");
    } catch { toast.error("Failed to set up 2FA"); }
  };

  const handleChangePassword = async () => {
    if (!fullName.trim()) return toast.error("Please enter your full name");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");
    const failing = PASSWORD_RULES.find(r => !r.test(newPassword));
    if (failing) return toast.error(failing.label);
    try {
      setLoading(true);
      const payload = { new_password: newPassword, name: fullName.trim() };
      if (smsConsent && smsPhone.trim()) {
        payload.sms_consent = true;
        payload.phone = smsPhone.trim();
      } else {
        payload.sms_consent = false;
      }
      const res = await api.post("/auth/change-password", payload);
      toast.success("Password updated");
      if (res.data.status === "totp_setup_required") await loadTotpSetup();
      else if (res.data.status === "totp_required") { setAuthStep("totp_verify"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setLoading(false); }
  };

  const handleVerifyTotp = async () => {
    if (totpCode.length !== 6) return toast.error("Enter the 6-digit code");
    try {
      setLoading(true);
      const res = await api.post("/auth/verify-totp", { code: totpCode });
      if (res.data.status === "authenticated") {
        // Cookie set by backend — no client-side token storage.
        toast.success("Signed in securely");
        window.location.href = getPostLoginDest();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code"); setTotpCode("");
    } finally { setLoading(false); }
  };

  const handleRecoveryCode = async () => {
    if (!recoveryInput.trim()) return toast.error("Enter a recovery code");
    try {
      setLoading(true);
      const res = await api.post("/auth/verify-recovery-code", { code: recoveryInput.trim() });
      if (res.data.status === "authenticated") {
        // Cookie set by backend — no client-side token storage.
        toast.success(`Signed in. ${res.data.remaining_codes} recovery codes remaining.`);
        window.location.href = getPostLoginDest();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid recovery code"); setRecoveryInput("");
    } finally { setLoading(false); }
  };

  const handleForgotRequest = async () => {
    if (!forgotEmail) return toast.error("Enter your email");
    try {
      setLoading(true);
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setResetSent(true);
      toast.success("If that email exists, a reset code has been sent");
    } catch (err) {
      toast.error("Something went wrong");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!resetCode || !resetNewPassword) return toast.error("Enter the code and new password");
    if (resetNewPassword !== resetConfirm) return toast.error("Passwords do not match");
    const failing = PASSWORD_RULES.find(r => !r.test(resetNewPassword));
    if (failing) return toast.error(failing.label);
    try {
      setLoading(true);
      const res = await api.post("/auth/reset-password", { code: resetCode.trim().toUpperCase(), email: forgotEmail, new_password: resetNewPassword });
      toast.success(res.data.message);
      setAuthStep("login"); setResetSent(false); setForgotEmail(""); setResetCode(""); setResetNewPassword(""); setResetConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Recovery codes copied to clipboard");
  };

  const stepIndicator = (
    <div className="flex items-center gap-2 mb-6">
      {["Sign In", "Register", "Set Up 2FA", "Verify"].map((label, i) => {
        const steps = { login: 0, change_password: 1, totp_setup: 2, totp_verify: 3 };
        const current = steps[authStep] || 0;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-6 h-6 flex items-center justify-center text-[10px] font-bold ${i < current ? "bg-[#16A34A] text-white" : i === current ? "bg-[#0055FF] text-white" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}>
              {i < current ? <CheckCircle size={12} weight="fill" /> : i + 1}
            </div>
            {i < 3 && <div className={`w-4 h-0.5 ${i < current ? "bg-[#16A34A]" : "bg-[#E5E5E5]"}`} />}
          </div>
        );
      })}
    </div>
  );

  const renderClientContent = () => {
    // Forgot password flow
    if (authStep === "forgot_password") {
      return (
        <div className="space-y-5">
          <button onClick={() => { setAuthStep("login"); setResetSent(false); }} className="flex items-center gap-1 text-xs text-[#0055FF] font-bold uppercase tracking-wider hover:underline mb-2">
            <ArrowLeft size={12} /> Back to Sign In
          </button>
          <div className="flex items-center gap-2 mb-2">
            <Lock size={18} className="text-[#0055FF]" />
            <h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Reset Password</h3>
          </div>
          {!resetSent ? (
            <div className="space-y-4">
              <p className="text-xs text-[#525252]">Enter your email and we'll send you a reset code.</p>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Email</Label>
                <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="you@company.com" className="rounded-none border-[#E5E5E5] h-11" data-testid="forgot-email-input" />
              </div>
              <Button onClick={handleForgotRequest} disabled={loading} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="forgot-submit">
                {loading ? "Sending..." : "Send Reset Code"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-[#525252]">Check your email for the 8-character reset code and enter it below.</p>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Reset Code</Label>
                <Input value={resetCode} onChange={e => setResetCode(e.target.value.toUpperCase())} placeholder="XXXXXXXX" className="rounded-none border-[#E5E5E5] h-11 text-center text-lg font-mono tracking-wider" maxLength={8} data-testid="reset-code-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">New Password</Label>
                <Input type="password" value={resetNewPassword} onChange={e => setResetNewPassword(e.target.value)} placeholder="New secure password" className="rounded-none border-[#E5E5E5] h-11" data-testid="reset-new-password" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Confirm Password</Label>
                <Input type="password" value={resetConfirm} onChange={e => setResetConfirm(e.target.value)} placeholder="Repeat password" className="rounded-none border-[#E5E5E5] h-11" data-testid="reset-confirm-password" />
              </div>
              <div className="border border-[#E5E5E5] p-3 space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">Requirements</p>
                {PASSWORD_RULES.map((rule, i) => {
                  const pass = resetNewPassword && rule.test(resetNewPassword);
                  return <div key={i} className={`flex items-center gap-2 text-xs ${pass ? "text-[#16A34A]" : "text-[#A3A3A3]"}`}><CheckCircle size={12} weight={pass ? "fill" : "regular"} /><span>{rule.label}</span></div>;
                })}
              </div>
              <Button onClick={handleResetPassword} disabled={loading} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="reset-submit">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (authStep === "change_password") {
      return (
        <div className="space-y-5">
          {stepIndicator}
          <div className="flex items-center gap-2 mb-2"><User size={18} className="text-[#0055FF]" /><h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Complete Your Registration</h3></div>
          <p className="text-xs text-[#525252]">Set up your profile and create a secure password to get started.</p>

          {/* Name */}
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Full Name</Label>
            <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" className="rounded-none border-[#E5E5E5] h-11" data-testid="registration-name-input" />
          </div>

          {/* Phone + SMS Consent */}
          <div className="space-y-3" data-testid="sms-consent-section">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Mobile Phone Number</Label>
              <Input
                type="tel"
                value={smsPhone}
                onChange={e => setSmsPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="rounded-none border-[#E5E5E5] h-11"
                data-testid="sms-phone-input"
              />
            </div>
            <div className="border border-[#E5E5E5] p-3">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="sms-consent-label">
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={e => setSmsConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded-none border-[#D4D4D4] text-[#0055FF] focus:ring-[#0055FF] cursor-pointer"
                  data-testid="sms-consent-checkbox"
                />
                <span className="text-xs text-[#525252] leading-relaxed">
                  I agree to receive SMS notifications about project updates from Eyvo. Message &amp; data rates may apply. Message frequency varies. Reply STOP to unsubscribe. Reply HELP for help.{" "}
                  <a href="https://www.eyvo.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline">Privacy Policy</a> &amp;{" "}
                  <a href="https://www.eyvo.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline">Terms of Service</a>.
                </span>
              </label>
            </div>
          </div>

          {/* Password Section */}
          <div className="border-t border-[#E5E5E5] pt-4 space-y-4">
            <div className="flex items-center gap-2"><Lock size={16} className="text-[#0055FF]" /><p className="text-xs font-bold uppercase tracking-wider text-[#525252]">Set Your Password</p></div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">New Password</Label>
              <div className="relative">
                <Input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="rounded-none border-[#E5E5E5] h-11 pr-10" placeholder="Enter new password" data-testid="new-password-input" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]">{showNewPassword ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Confirm Password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="rounded-none border-[#E5E5E5] h-11" placeholder="Repeat password" data-testid="confirm-password-input" />
            </div>
            <div className="border border-[#E5E5E5] p-3 space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">Requirements</p>
              {PASSWORD_RULES.map((rule, i) => { const pass = newPassword && rule.test(newPassword); return <div key={i} className={`flex items-center gap-2 text-xs ${pass ? "text-[#16A34A]" : "text-[#A3A3A3]"}`}><CheckCircle size={12} weight={pass ? "fill" : "regular"} /><span>{rule.label}</span></div>; })}
            </div>
          </div>

          <Button onClick={handleChangePassword} disabled={loading} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="change-password-button">{loading ? "Creating Account..." : "Complete Registration"}</Button>
        </div>
      );
    }

    if (authStep === "totp_setup") {
      return (
        <div className="space-y-5">
          {stepIndicator}
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={18} className="text-[#0055FF]" /><h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Set Up Two-Factor Authentication</h3></div>
          <p className="text-xs text-[#525252]">Scan the QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.)</p>
          <div className="flex justify-center py-3">
            {qrCode && <img src={`data:image/png;base64,${qrCode}`} alt="TOTP QR Code" className="w-44 h-44" data-testid="totp-qr-code" />}
          </div>
          <div className="border border-[#E5E5E5] p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">Can't scan? Enter manually:</p>
            <code className="text-sm font-mono text-[#0A0A0A] select-all break-all" data-testid="totp-secret-key">{secretKey}</code>
          </div>
          {/* Recovery Codes */}
          {recoveryCodes.length > 0 && (
            <div className="border border-[#DC2626]/20 bg-[#FEF2F2] p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#DC2626] flex items-center gap-1.5"><Warning size={14} /> Recovery Backup Codes</p>
                <button onClick={copyRecoveryCodes} className="flex items-center gap-1 text-[10px] font-bold text-[#0055FF] hover:underline" data-testid="copy-recovery-codes"><Copy size={12} /> Copy All</button>
              </div>
              <p className="text-[10px] text-[#525252] mb-3">Save these codes securely. Each can be used once if you lose access to your authenticator app.</p>
              <div className="grid grid-cols-2 gap-1.5">
                {recoveryCodes.map((code, i) => (
                  <code key={i} className="bg-white border border-[#E5E5E5] px-2 py-1 text-xs font-mono text-[#0A0A0A] text-center select-all" data-testid={`recovery-code-${i}`}>{code}</code>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Enter 6-digit verification code</Label>
            <Input value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="rounded-none border-[#E5E5E5] h-11 text-center text-xl font-mono tracking-[0.5em]" placeholder="000000" maxLength={6} data-testid="totp-code-input" />
          </div>
          <Button onClick={handleVerifyTotp} disabled={loading || totpCode.length !== 6} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="verify-totp-button">{loading ? "Verifying..." : "Verify & Complete Setup"}</Button>
        </div>
      );
    }

    if (authStep === "totp_verify") {
      if (showRecoveryInput) {
        return (
          <div className="space-y-5">
            {stepIndicator}
            <button onClick={() => setShowRecoveryInput(false)} className="flex items-center gap-1 text-xs text-[#0055FF] font-bold uppercase tracking-wider hover:underline"><ArrowLeft size={12} /> Back to code entry</button>
            <div className="flex items-center gap-2 mb-2"><Key size={18} className="text-[#D97706]" /><h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Use Recovery Code</h3></div>
            <p className="text-xs text-[#525252]">Enter one of your backup recovery codes to sign in.</p>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Recovery Code</Label>
              <Input value={recoveryInput} onChange={e => setRecoveryInput(e.target.value.toUpperCase())} placeholder="XXXX-XXXX" className="rounded-none border-[#E5E5E5] h-11 text-center text-lg font-mono tracking-wider" maxLength={9} data-testid="recovery-code-input" />
            </div>
            <Button onClick={handleRecoveryCode} disabled={loading || !recoveryInput.trim()} className="w-full bg-[#D97706] hover:bg-[#B45309] text-white font-bold rounded-none h-11" data-testid="recovery-submit">{loading ? "Verifying..." : "Sign In with Recovery Code"}</Button>
          </div>
        );
      }

      return (
        <div className="space-y-5">
          {stepIndicator}
          <div className="flex items-center gap-2 mb-2"><Key size={18} className="text-[#0055FF]" /><h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Two-Factor Authentication</h3></div>
          <p className="text-xs text-[#525252]">Enter the 6-digit code from your authenticator app.</p>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Verification Code</Label>
            <Input value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={e => { if (e.key === "Enter" && totpCode.length === 6) handleVerifyTotp(); }} className="rounded-none border-[#E5E5E5] h-11 text-center text-xl font-mono tracking-[0.5em]" placeholder="000000" maxLength={6} autoFocus data-testid="totp-verify-input" />
          </div>
          <Button onClick={handleVerifyTotp} disabled={loading || totpCode.length !== 6} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="verify-totp-submit">{loading ? "Verifying..." : "Verify & Sign In"}</Button>
          <button onClick={() => setShowRecoveryInput(true)} className="w-full text-center text-xs text-[#A3A3A3] hover:text-[#0055FF] font-medium py-2" data-testid="use-recovery-code">Lost your authenticator? Use a recovery code</button>
        </div>
      );
    }

    // Client sign-up form
    if (authStep === "client_signup") {
      return (
        <form onSubmit={handleClientSignup} className="space-y-4">
          <button type="button" onClick={() => setAuthStep("login")} className="flex items-center gap-1 text-xs text-[#0055FF] font-bold uppercase tracking-wider hover:underline mb-2" data-testid="back-to-signin">
            <ArrowLeft size={12} /> Back to Sign In
          </button>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Full Name</Label>
            <Input value={signupName} onChange={e => setSignupName(e.target.value)} placeholder="Your full name" className="rounded-none border-[#E5E5E5] h-11" data-testid="signup-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Email</Label>
            <Input type="email" value={signupEmail} onChange={e => { setSignupEmail(e.target.value); setSignupDomainStatus(null); }} onBlur={e => checkSignupDomain(e.target.value)} placeholder="you@company.com" className={`rounded-none h-11 ${signupDomainStatus && !signupDomainStatus.allowed && signupDomainStatus !== "checking" ? "border-[#DC2626]" : "border-[#E5E5E5] focus:border-[#0055FF]"}`} data-testid="signup-email-input" />
            {signupDomainStatus === "checking" && <p className="text-[10px] text-[#A3A3A3] flex items-center gap-1"><span className="w-3 h-3 border border-[#0055FF] border-t-transparent animate-spin inline-block" /> Checking...</p>}
            {signupDomainStatus && signupDomainStatus.allowed === false && (
              <div className="bg-[#FEF2F2] border border-[#DC2626]/20 p-2.5 flex items-start gap-2" data-testid="signup-domain-rejected"><ShieldCheck size={14} className="text-[#DC2626] flex-shrink-0 mt-0.5" /><p className="text-xs text-[#DC2626] font-medium">{signupDomainStatus.message}</p></div>
            )}
            {signupDomainStatus && signupDomainStatus.allowed === true && <p className="text-[10px] text-[#16A34A] flex items-center gap-1" data-testid="signup-domain-accepted"><CheckCircle size={12} weight="fill" /> {signupDomainStatus.tenant_name ? `Recognized: ${signupDomainStatus.tenant_name}` : "Domain recognized"}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Mobile Phone Number <span className="text-[#A3A3A3] font-normal normal-case tracking-normal">(optional)</span></Label>
            <Input type="tel" value={signupPhone} onChange={e => setSignupPhone(e.target.value)} placeholder="+1 (555) 000-0000" className="rounded-none border-[#E5E5E5] h-11" data-testid="signup-phone-input" />
          </div>
          <div className="border border-[#E5E5E5] p-3">
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">Optional — SMS Notifications</p>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="signup-sms-consent-label">
              <input type="checkbox" checked={signupSmsConsent} onChange={e => setSignupSmsConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded-none border-[#D4D4D4] text-[#0055FF] focus:ring-[#0055FF] cursor-pointer" data-testid="signup-sms-consent-checkbox" />
              <span className="text-xs text-[#525252] leading-relaxed">
                I agree to receive SMS notifications about project updates from Eyvo. Message &amp; data rates may apply. Message frequency varies. Reply STOP to unsubscribe. Reply HELP for help.{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline">Privacy Policy</a> &amp;{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline">Terms of Service</a>.
              </span>
            </label>
            <p className="text-[10px] text-[#A3A3A3] mt-2 ml-7">You can create your account without opting in to SMS. This is entirely optional.</p>
          </div>
          <div className="border-t border-[#E5E5E5] pt-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Password</Label>
              <div className="relative">
                <Input type={showSignupPassword ? "text" : "password"} value={signupPassword} onChange={e => setSignupPassword(e.target.value)} className="rounded-none border-[#E5E5E5] h-11 pr-10" placeholder="Create a strong password" data-testid="signup-password-input" />
                <button type="button" onClick={() => setShowSignupPassword(!showSignupPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]">{showSignupPassword ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Confirm Password</Label>
              <Input type="password" value={signupConfirm} onChange={e => setSignupConfirm(e.target.value)} className="rounded-none border-[#E5E5E5] h-11" placeholder="Repeat password" data-testid="signup-confirm-input" />
            </div>
            <div className="border border-[#E5E5E5] p-3 space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">Requirements</p>
              {PASSWORD_RULES.map((rule, i) => { const pass = signupPassword && rule.test(signupPassword); return <div key={i} className={`flex items-center gap-2 text-xs ${pass ? "text-[#16A34A]" : "text-[#A3A3A3]"}`}><CheckCircle size={12} weight={pass ? "fill" : "regular"} /><span>{rule.label}</span></div>; })}
            </div>
          </div>
          <Button type="submit" disabled={loading || (signupDomainStatus && signupDomainStatus.allowed === false)} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="signup-submit-button">{loading ? "Creating Account..." : "Create Account"}</Button>
          <div className="flex items-center gap-2 pt-1 text-[10px] text-[#A3A3A3]"><ShieldCheck size={12} className="text-[#16A34A]" /><span>Your account will be secured with two-factor authentication</span></div>
        </form>
      );
    }

    // Default: login form
    return (
      <form onSubmit={handleEmailLogin} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setDomainStatus(null); }} onBlur={(e) => checkDomain(e.target.value)} placeholder="you@company.com" className={`rounded-none h-11 ${domainStatus && !domainStatus.allowed && domainStatus !== "checking" ? "border-[#DC2626]" : "border-[#E5E5E5] focus:border-[#0055FF]"}`} data-testid="email-input" />
          {domainStatus === "checking" && <p className="text-[10px] text-[#A3A3A3] flex items-center gap-1"><span className="w-3 h-3 border border-[#0055FF] border-t-transparent animate-spin inline-block" /> Checking...</p>}
          {domainStatus && domainStatus.allowed === false && (
            <div className="bg-[#FEF2F2] border border-[#DC2626]/20 p-2.5 flex items-start gap-2" data-testid="domain-rejected"><ShieldCheck size={14} className="text-[#DC2626] flex-shrink-0 mt-0.5" /><p className="text-xs text-[#DC2626] font-medium">{domainStatus.message}</p></div>
          )}
          {domainStatus && domainStatus.allowed === true && <p className="text-[10px] text-[#16A34A] flex items-center gap-1" data-testid="domain-accepted"><CheckCircle size={12} weight="fill" /> {domainStatus.tenant_name ? `Recognized: ${domainStatus.tenant_name}` : "Domain recognized"}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Password</Label>
          <div className="relative">
            <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="rounded-none border-[#E5E5E5] focus:border-[#0055FF] h-11 pr-10" data-testid="password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]">{showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
          </div>
        </div>
        <Button type="submit" disabled={loading || (domainStatus && domainStatus.allowed === false)} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-11" data-testid="login-submit-button">{loading ? "Signing in..." : "Sign In"}</Button>
        <div className="flex items-center justify-between pt-1">
          <button type="button" onClick={() => setAuthStep("forgot_password")} className="text-xs text-[#A3A3A3] hover:text-[#0055FF] font-medium" data-testid="forgot-password-link">Forgot your password?</button>
          <button type="button" onClick={() => setAuthStep("client_signup")} className="text-xs text-[#0055FF] hover:underline font-bold" data-testid="create-account-link">Create an account</button>
        </div>
        {/* Session 75 — onboarding-guide link for brand-new clients. The
            /guide/client route is public (App.js) so this works pre-login. */}
        <a
          href="/guide/client#getting-started"
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-xs text-[#525252] hover:text-[#0055FF] underline decoration-dotted underline-offset-4 pt-1"
          data-testid="client-onboarding-guide-link"
        >
          New here? Read the step-by-step onboarding guide →
        </a>
        <div className="flex items-center gap-2 pt-1 text-[10px] text-[#A3A3A3]"><ShieldCheck size={12} className="text-[#16A34A]" /><span>Protected by two-factor authentication &amp; encrypted at rest</span></div>
      </form>
    );
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-white border-r border-[#E5E5E5]">
        <div className="absolute inset-0 flex flex-col items-center justify-center px-12">
          <img src="/eyvo-logo.png" alt="Eyvo" className="w-40 h-auto mb-8" />
          <h1 className="text-3xl sm:text-4xl font-black text-[#1a2b6d] tracking-tight leading-tight text-center mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Welcome to Eyvo <span className="pulse-beat" style={{ color: "var(--theme-primary, #0055FF)" }}>Pulse</span></h1>
          <p className="text-base text-[#525252] text-center">Client Project Delivery and Support Platform.</p>
        </div>
        <div className="absolute bottom-8 left-0 right-0 text-center"><p className="text-xs text-[#8a94b8] tracking-wider">Designed by Eyvo, California, USA</p><p className="text-[10px] text-[#D4D4D4] mt-1" data-testid="app-release-label">{LATEST_RELEASE_LABEL}</p></div>
      </div>
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-20 bg-white">
        <div className="max-w-sm w-full mx-auto">
          <div className="lg:hidden mb-10 flex flex-col items-center">
            <img src="/eyvo-logo.png" alt="Eyvo" className="w-24 h-auto mb-4" />
            <p className="text-sm text-[#1a2b6d] text-center font-bold">Welcome to Eyvo Pulse</p>
            <p className="text-xs text-[#525252] text-center mt-1">Client Project Delivery and Support Platform.</p>
          </div>
          {authStep === "login" || authStep === "forgot_password" || authStep === "client_signup" ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {authStep === "client_signup" ? "Create account" : "Sign in"}
                </h2>
                <p className="text-sm text-[#525252] mt-1">
                  {authStep === "client_signup" ? "Register as a client to access your projects" : "Access your projects and team workspace"}
                </p>
              </div>
              {authStep === "forgot_password" || authStep === "client_signup" ? renderClientContent() : (
                <Tabs defaultValue="eyvo" className="w-full">
                  <TabsList className="w-full grid grid-cols-2 mb-6 bg-[#F5F5F5] rounded-none h-10">
                    <TabsTrigger value="eyvo" className="rounded-none text-xs font-bold uppercase tracking-wider data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-eyvo-login">Eyvo Team</TabsTrigger>
                    <TabsTrigger value="client" className="rounded-none text-xs font-bold uppercase tracking-wider data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-client-login">Client</TabsTrigger>
                  </TabsList>
                  <TabsContent value="eyvo" className="space-y-6">
                    <div className="border border-[#E5E5E5] p-6">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-4">Google Workspace</p>
                      <p className="text-sm text-[#525252] mb-6">Sign in with your @eyvo.com Google account</p>
                      <div data-testid="google-login-button"><GoogleLogin onSuccess={handleGoogleSuccess} onError={(err) => { console.error("Google OAuth error:", err); toast.error("Google sign-in failed. Please check that popups are allowed and try again."); }} size="large" width="100%" text="signin_with" shape="rectangular" /></div>
                    </div>
                  </TabsContent>
                  <TabsContent value="client" className="space-y-6">{renderClientContent()}</TabsContent>
                </Tabs>
              )}
            </>
          ) : (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Secure Setup</h2>
                <p className="text-sm text-[#525252] mt-1">{email}</p>
              </div>
              {renderClientContent()}
            </div>
          )}
          <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
            <div className="flex items-center justify-center gap-3 text-xs text-[#A3A3A3]">
              <Link to="/privacy" className="hover:text-[#0055FF] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <span>&middot;</span>
              <Link to="/terms" className="hover:text-[#0055FF] transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            </div>
            {/* Database Connection Status */}
            <div className="mt-4 flex items-center justify-center gap-2" data-testid="db-status-indicator">
              <div className={`w-2 h-2 rounded-full ${
                dbStatus.database === "connected"
                  ? "bg-[#16A34A] animate-pulse"
                  : dbStatus.status === "checking"
                  ? "bg-[#D97706] animate-pulse"
                  : "bg-[#DC2626] animate-pulse"
              }`} />
              <span className={`text-[10px] font-bold uppercase tracking-wider ${
                dbStatus.database === "connected" ? "text-[#16A34A]" : dbStatus.status === "checking" ? "text-[#D97706]" : "text-[#DC2626]"
              }`}>
                {dbStatus.database === "connected" ? "Connected" : dbStatus.status === "checking" ? "Connecting..." : "Disconnected"}
              </span>
            </div>
            {dbStatus.db_info && (
              <p className="text-[10px] text-[#A3A3A3] text-center mt-1 font-mono" data-testid="db-connection-string">
                {dbStatus.db_info}
              </p>
            )}
            {dbStatus.driver_error && (
              <p className="text-[10px] text-[#DC2626] text-center mt-1" data-testid="db-driver-error">{dbStatus.driver_error}</p>
            )}
            <p className="text-xs text-[#A3A3A3] text-center tracking-wider mt-3 lg:hidden">Designed by Eyvo, California, USA</p>
            <p className="text-[10px] text-[#D4D4D4] text-center mt-3" data-testid="app-version">{LATEST_RELEASE_LABEL}</p>
          </div>
        </div>
      </div>
      {/* Version on left panel */}
      <style>{`.login-version-left { position: absolute; bottom: 28px; left: 0; right: 0; }`}</style>
    </div>
  );
}
